import { createUseStyles } from 'react-jss';
import {
  cssSpacingSm,
  formUi,
  inputMaxWidth,
  maxTableWidth,
} from '../constants/globalcss';
import React from 'react';
import { fieldValidation } from '../utils/validation';
import { getFieldRenderer } from './fieldRenderers';
import { ILabel } from '../constants/interfaces';
import { IFormStateValue } from '../utils/formUtils';

export interface IFormFieldProps {
  value: string;
  handleInputChange?: (key: number, value: string, children?: []) => void;
  errorText?: string;
  field: ILabel;
  /** Full request form state — used to evaluate visibility on nested line-item custom fields. */
  formStateForVisibility?: Record<string, IFormStateValue>;
  isLabelHidden?: boolean;
  displayOnly?: boolean;
  isInGrid?: boolean;
  resetTrigger?: number;
  width?: string;
  hasTableField?: boolean;
  onBlur?: () => void;
}

const useStyles = createUseStyles({
  input: {
    alignItems: 'stretch',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: cssSpacingSm,
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: formUi.field.verticalGap,
    paddingBlock: '3px',
    paddingInline: 0,
    width: '100%',
  },
  inputNoLabel: {
    alignItems: 'stretch',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: cssSpacingSm,
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: formUi.field.verticalGap,
    paddingBlock: '3px',
    paddingInline: 0,
    width: '100%',
  },
});

function getFieldWidthStyle(props: IFormFieldProps, field: ILabel) {
  const { width, isInGrid, hasTableField } = props;
  if (hasTableField) {
    // Use 100% width but never exceed maxTableWidth or viewport width
    return {
      maxWidth: Math.min(window.innerWidth, maxTableWidth),
      width: '100%',
    };
  }
  if (getFieldRenderer(field?.type)?.stretch) {
    return {
      alignItems: 'stretch',
      alignSelf: 'stretch',
      display: 'flex',
      flexDirection: 'column',
      maxWidth: '100%',
      minWidth: 0,
      width: '100%',
    };
  }
  if (width) return { width };
  if (isInGrid && getFieldRenderer(field?.type)?.fullWidthInGrid) {
    return { width: '100%' };
  }
  return {
    maxWidth: inputMaxWidth + 400,
    width: '100%',
  };
}

function FormField(props: IFormFieldProps) {
  const {
    field,
    value,
    handleInputChange,
    formStateForVisibility,
    isLabelHidden,
    displayOnly,
    width = '100%',
    onBlur,
  } = props;
  const [errorText, setErrorText] = React.useState<string | undefined>(
    props.errorText
  );
  const classes = useStyles();

  React.useEffect(() => {
    setErrorText(props.errorText);
  }, [props.errorText]);

  const onChange = React.useCallback(
    (value, children?, labelId?: number) => {
      const error = fieldValidation(field.type, value, field.isMandatory);
      setErrorText(error);
      if (handleInputChange) {
        handleInputChange(labelId || field.labelId, value, children);
      }
    },
    [field, handleInputChange]
  );

  const onAuxiliaryFieldChange = React.useCallback(
    (key: number, text: string) => {
      if (handleInputChange) {
        handleInputChange(key, text, []);
      }
    },
    [handleInputChange]
  );

  // Handle validation errors from child components (like DateField rule violations)
  const onError = React.useCallback((error: string) => {
    setErrorText(error || undefined);
  }, []);

  const commonProps = React.useMemo(
    () => ({
      displayOnly,
      errorText,
      field,
      isLabelHidden,
      onBlur,
      onChange,
      onError,
      value,
      width: width,
    }),
    [
      displayOnly,
      errorText,
      field,
      isLabelHidden,
      onBlur,
      onChange,
      onError,
      value,
      width,
    ]
  );

  // Rendering is driven by the label's configured type: see fieldRenderers.
  const renderInputComponent = React.useCallback(() => {
    const renderer = getFieldRenderer(field?.type);
    if (!renderer) {
      return null;
    }
    return renderer.render({
      commonProps,
      field,
      formStateForVisibility,
      isInGrid: props.isInGrid,
      onAuxiliaryFieldChange,
      onChange,
      resetTrigger: props.resetTrigger,
    });
  }, [
    field,
    commonProps,
    formStateForVisibility,
    onAuxiliaryFieldChange,
    onChange,
    props.isInGrid,
    props.resetTrigger,
  ]);

  return (
    <div
      className={isLabelHidden ? classes.inputNoLabel : classes.input}
      style={getFieldWidthStyle(props, field)}
    >
      {renderInputComponent()}
    </div>
  );
}

export default FormField;
